import { Button, Checkbox, Group, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { SSHKey } from '../../../../../../@types/sshKey';

interface SSHKeyConfigurationBoxProps {
    sshKeys: SSHKey[];
    selectedKeys: string[];
    onChangeSelectedKeys: (keys: string[]) => void;
    onAddNewKey?: () => void;
    isLoading?: boolean;
}

export default function SSHKeyConfigurationBox({
    sshKeys,
    selectedKeys,
    onChangeSelectedKeys,
    onAddNewKey,
    isLoading = false,
}: SSHKeyConfigurationBoxProps) {
    const { t } = useTranslation('cloudComputeDeployment');
    const toggleKey = (content: string) => {
        if (selectedKeys.includes(content)) {
            onChangeSelectedKeys(selectedKeys.filter((key) => key !== content));
            return;
        }

        onChangeSelectedKeys([...selectedKeys, content]);
    };

    const renderKeyRows = () => {
        return sshKeys.map((key, idx) => {
            const checked = selectedKeys.includes(key.content);

            return (
                <label key={`${key.id}-${idx}`} className="block cursor-pointer">
                    <Paper
                        radius="md"
                        p="md"
                        withBorder
                        style={{
                            width: '100%',
                            textAlign: 'left',
                            backgroundColor: checked
                                ? 'var(--color-selected-bg)'
                                : 'var(--surface-2)',
                            borderColor: checked ? 'rgba(70, 132, 255, 1)' : 'var(--line)',
                        }}
                    >
                        <Group justify="space-between" align="center" wrap="nowrap">
                            <Group gap="md" align="center" wrap="nowrap">
                                <Checkbox
                                    checked={checked}
                                    onChange={() => toggleKey(key.content)}
                                    color="blue"
                                    radius="sm"
                                />
                                <Stack gap={2} style={{ minWidth: 0 }}>
                                    <Text fw={500} size="md" c="var(--fg)" ff="monospace">
                                        {key.name}
                                    </Text>
                                </Stack>
                            </Group>
                            <Text size="sm" c="dimmed" ff="monospace" style={{ minWidth: 0 }}>
                                {key.type.toUpperCase()}:
                                {key.content.slice(20, 30) + '...' + key.content.slice(30, 33)}
                            </Text>
                        </Group>
                    </Paper>
                </label>
            );
        });
    };

    if (isLoading) {
        return (
            <Stack gap="sm">
                <Paper
                    radius="md"
                    p="md"
                    withBorder
                    style={{ backgroundColor: 'var(--surface-2)' }}
                >
                    <Text c="dimmed">{t('sshKey.loading')}</Text>
                </Paper>
            </Stack>
        );
    }

    if (sshKeys.length === 0) {
        return (
            <Stack gap="sm">
                <Stack p="md" align="center" justify="center" gap={4}>
                    <Text fw={500} c="var(--fg)">
                        {t('sshKey.noKeysTitle')}
                    </Text>
                    <Text size="sm" c="dimmed" mt={4}>
                        {t('sshKey.noKeysDescription')}
                    </Text>
                </Stack>
                <Button
                    variant="outline"
                    color="gray"
                    radius="md"
                    onClick={onAddNewKey}
                    className="h-16 w-full rounded-md border border-dashed border-line bg-transparent text-fg/75 transition-colors duration-150 hover:border-[#4684ff] hover:bg-[rgba(70,132,255,0.06)] hover:text-fg/95"
                    style={{
                        borderStyle: 'dashed',
                        borderColor: 'var(--line)',
                        borderWidth: '1px',
                    }}
                >
                    <IconPlus size={16} stroke={2.5} className="mr-2" />
                    <span className="text-sm text-fg/68">{t('sshKey.addNew')}</span>
                </Button>
            </Stack>
        );
    }

    return (
        <Stack gap="sm" w={'100%'}>
            <div className="w-full p-3 pl-5 pt-5 rounded-md border border-line overflow-hidden">
                <ScrollArea
                    mah={300}
                    type="auto"
                    offsetScrollbars
                    scrollbarSize={8}
                    style={{ width: '100%', height: 'auto', maxHeight: 300 }}
                >
                    <Stack gap={'xs'}>{renderKeyRows()}</Stack>
                </ScrollArea>
            </div>
            <Button
                variant="outline"
                color="gray"
                radius="md"
                onClick={onAddNewKey}
                className="h-12 w-full rounded-md border border-dashed border-line bg-surface-2 text-fg/68 transition-colors duration-150 hover:border-[#4684ff] hover:bg-[rgba(70,132,255,0.06)] hover:text-fg/95"
                style={{
                    borderStyle: 'dashed',
                    borderColor: 'var(--line)',
                    borderWidth: '1px',
                }}
            >
                <IconPlus size={16} stroke={2.5} className="mr-2" />
                <span className="text-sm text-fg/68">{t('sshKey.addNew')}</span>
            </Button>
        </Stack>
    );
}
